// src/hooks/useAnnouncement.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth/AuthContext';
import { getActiveAnnouncement } from '../constants/announcements';

const storageKey = (id) => `announcementSeen:${id}`;

const wasSeenLocally = (id) => {
  try {
    return Boolean(localStorage.getItem(storageKey(id)));
  } catch (error) {
    return false;
  }
};

const markSeenLocally = (id) => {
  try {
    localStorage.setItem(storageKey(id), String(Date.now()));
  } catch (error) {
    // Storage unavailable (private browsing). Firestore remains the source of truth.
  }
};

const isForced = (id) => {
  try {
    return new URLSearchParams(window.location.search).get('showAnnouncement') === id;
  } catch (error) {
    return false;
  }
};

/**
 * Decides whether the active one-time announcement should be shown to the
 * current user, and records the dismissal so it never shows again.
 *
 * The local storage key is a fast path and a safety net: it avoids a Firestore
 * read on every page load for users who have already dismissed, and it keeps the
 * dialog from reappearing in this browser if the Firestore write fails.
 */
export const useAnnouncement = () => {
  const { user, loading: authLoading } = useAuth();
  const announcement = getActiveAnnouncement();
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const isMounted = useRef(true);
  const checkedUid = useRef(null);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!announcement || authLoading) {
      return;
    }

    if (!user?.uid) {
      checkedUid.current = null;
      setOpen(false);
      return;
    }

    if (checkedUid.current === user.uid) {
      return;
    }
    checkedUid.current = user.uid;

    const checkSeenState = async () => {
      if (isForced(announcement.id)) {
        if (isMounted.current) {
          setOpen(true);
          setReady(true);
        }
        return;
      }

      if (wasSeenLocally(announcement.id)) {
        if (isMounted.current) {
          setReady(true);
        }
        return;
      }

      try {
        const snapshot = await getDoc(doc(db, 'users', user.uid));
        const seen = snapshot.data()?.announcementsSeen?.[announcement.id];

        if (seen) {
          markSeenLocally(announcement.id);
        }

        if (isMounted.current) {
          setOpen(!seen);
        }
      } catch (error) {
        // Stay quiet rather than risk showing an already-dismissed announcement.
        console.error('Error reading announcement state:', error);
      } finally {
        if (isMounted.current) {
          setReady(true);
        }
      }
    };

    checkSeenState();
  }, [announcement, authLoading, user]);

  const dismiss = useCallback(async () => {
    if (!announcement) {
      return;
    }

    setOpen(false);
    markSeenLocally(announcement.id);

    if (!user?.uid) {
      return;
    }

    try {
      // merge deep-merges maps, so sibling announcement ids and the rest of the
      // user document (consent, login tracking) are preserved.
      await setDoc(
        doc(db, 'users', user.uid),
        {
          announcementsSeen: {
            [announcement.id]: {
              seenAt: Date.now(),
              version: announcement.version,
            },
          },
        },
        { merge: true }
      );
    } catch (error) {
      console.error('Error saving announcement dismissal:', error);
    }
  }, [announcement, user]);

  return { announcement, open, dismiss, ready };
};

export default useAnnouncement;
